import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { FaFileExcel } from 'react-icons/fa';
import { MdLocationCity, MdOutlineFileUpload } from 'react-icons/md';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/AdminHeader';
import LayoutContainer from '../../components/LayoutContainer';
import elementItemService from '../../services/elementItemService';
import eventService from '../../services/eventService';
import { downloadElementReport } from './downloadElementReport';

const displayValue = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return 'empty';
  return text.length > 42 ? `${text.slice(0, 42)}…` : text;
};

const statusLabel = (value) => {
  if (value === 'verified') return 'Verified';
  if (value === 'discrepancy') return 'Discrepancy';
  if (value === 'exceeded') return 'Exceeded';
  return 'empty';
};

const showEditSuccess = (itemName, changes) => {
  toast.success(
    <div
      style={{
        fontFamily: 'Roboto, sans-serif',
        textAlign: 'left',
        width: '100%',
        paddingRight: 8,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: '#067647',
        }}
      >
        UPDATED
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 16,
          fontWeight: 700,
          color: '#2F3542',
          lineHeight: 1.3,
        }}
      >
        {itemName}
      </div>
      {changes.map((change) => (
        <div
          key={change.label}
          style={{ marginTop: 8, fontSize: 14, color: '#344054', lineHeight: 1.45 }}
        >
          <span style={{ fontWeight: 700, color: '#067647' }}>{change.label}</span>
          {' updated from '}
          <span
            style={{
              fontWeight: 600,
              color: '#98a2b3',
              textDecoration: 'line-through',
            }}
          >
            {change.from}
          </span>
          {' to '}
          <span
            style={{
              display: 'inline-block',
              marginLeft: 2,
              padding: '1px 8px',
              borderRadius: 999,
              backgroundColor: '#067647',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            {change.to}
          </span>
        </div>
      ))}
    </div>,
    {
      position: 'top-center',
      autoClose: 3500,
      closeButton: ({ closeToast }) => (
        <button
          type='button'
          aria-label='Close'
          onClick={closeToast}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            display: 'grid',
            placeItems: 'center',
            width: 26,
            height: 26,
            border: 'none',
            borderRadius: '50%',
            background: '#d1fadf',
            color: '#067647',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      ),
      style: {
        marginTop: '28vh',
        minWidth: 340,
        maxWidth: 460,
        borderRadius: 14,
        background: '#f6fef9',
        border: '1px solid #abefc6',
        borderLeft: '6px solid #12b76a',
        boxShadow: '0 18px 40px rgba(6, 118, 71, 0.16)',
        padding: '16px 18px',
      },
      progressStyle: { background: '#12b76a' },
    },
  );
};

const centerToastOptions = {
  position: 'top-center',
  autoClose: 3500,
  closeButton: ({ closeToast }) => (
    <button
      type='button'
      aria-label='Close'
      onClick={closeToast}
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        display: 'grid',
        placeItems: 'center',
        width: 26,
        height: 26,
        border: 'none',
        borderRadius: '50%',
        background: '#d1fadf',
        color: '#067647',
        cursor: 'pointer',
        padding: 0,
      }}
    >
      <CloseIcon sx={{ fontSize: 16 }} />
    </button>
  ),
  style: {
    marginTop: '28vh',
    minWidth: 340,
    maxWidth: 460,
    borderRadius: 14,
    background: '#f6fef9',
    border: '1px solid #abefc6',
    borderLeft: '6px solid #12b76a',
    boxShadow: '0 18px 40px rgba(6, 118, 71, 0.16)',
    padding: '16px 18px',
  },
  progressStyle: { background: '#12b76a' },
};

const showActionSuccess = (label, itemName) => {
  toast.success(
    <div
      style={{
        fontFamily: 'Roboto, sans-serif',
        textAlign: 'left',
        width: '100%',
        paddingRight: 8,
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: '#067647',
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 4,
          fontSize: 16,
          fontWeight: 700,
          color: '#2F3542',
          lineHeight: 1.3,
        }}
      >
        {itemName || 'Item'}
      </div>
    </div>,
    centerToastOptions,
  );
};

const ElementItemRow = memo(function ElementItemRow({
  row,
  index,
  saving,
  onSave,
  canEditReview,
  onSaveReview,
  mobile,
  showUpdatedBy,
  editLocked,
  onDelete,
  highlighted,
}) {
  const [draft, setDraft] = useState({
    particulars: String(row.particulars ?? '').toUpperCase(),
    dimensions: row.dimensions ?? '',
    quantity: row.quantity ?? '',
    unit: row.unit ?? '',
  });
  const [review, setReview] = useState({
    status: row.status || '',
    remarks: row.remarks || '',
  });
  const draftRef = useRef(draft);
  const reviewRef = useRef(review);

  useEffect(() => {
    const next = {
      particulars: String(row.particulars ?? '').toUpperCase(),
      dimensions: row.dimensions ?? '',
      quantity: row.quantity ?? '',
      unit: row.unit ?? '',
    };
    const nextReview = {
      status: row.status || '',
      remarks: row.remarks || '',
    };
    draftRef.current = next;
    reviewRef.current = nextReview;
    setDraft(next);
    setReview(nextReview);
  }, [row.id]);

  const changeDraft = (field, value) => {
    const nextValue = field === 'particulars' ? String(value).toUpperCase() : value;
    const next = { ...draftRef.current, [field]: nextValue };
    draftRef.current = next;
    setDraft(next);
    return next;
  };

  const changeReview = (field, value) => {
    const next = { ...reviewRef.current, [field]: value };
    reviewRef.current = next;
    setReview(next);
    return next;
  };

  const remarksLocked =
    editLocked ||
    !canEditReview ||
    (review.status !== 'discrepancy' && review.status !== 'exceeded');
  const fieldStyle = mobile ? mobileInputStyle : desktopInputStyle;
  const lockedFieldStyle = editLocked
    ? { ...fieldStyle, background: '#f7f9fc' }
    : fieldStyle;

  const fieldInput = (field, style = lockedFieldStyle) => (
    <input
      value={draft[field]}
      disabled={editLocked || saving}
      onChange={(event) => changeDraft(field, event.target.value)}
      onBlur={() => {
        if (!editLocked) onSave(row, draftRef.current);
      }}
      style={editLocked ? { ...style, background: '#f7f9fc' } : style}
    />
  );

  const statusField = (
    <StatusSelect
      value={review.status}
      disabled={editLocked || !canEditReview || saving}
      mobile={mobile}
      onChange={(event) => {
        if (editLocked) return;
        const next = changeReview('status', event.target.value);
        onSaveReview(row, next);
      }}
    />
  );

  const unitField = (
    <UnitSelect
      value={draft.unit}
      disabled={editLocked || saving}
      mobile={mobile}
      onChange={(event) => {
        if (editLocked) return;
        const next = changeDraft('unit', event.target.value);
        onSave(row, next);
      }}
    />
  );

  const remarksStyle = {
    ...(mobile ? fieldStyle : remarksDesktopStyle),
    background: remarksLocked ? '#f7f9fc' : '#fff',
  };

  const remarksField = mobile ? (
    <input
      value={review.remarks}
      disabled={remarksLocked || saving}
      onChange={(event) => changeReview('remarks', event.target.value)}
      onBlur={() => onSaveReview(row, reviewRef.current)}
      style={remarksStyle}
    />
  ) : (
    <textarea
      value={review.remarks}
      disabled={remarksLocked || saving}
      rows={1}
      onChange={(event) => changeReview('remarks', event.target.value)}
      onBlur={() => onSaveReview(row, reviewRef.current)}
      style={remarksStyle}
    />
  );

  if (mobile) {
    return (
      <Box
        id={`element-item-${row.id}`}
        sx={{
          p: 1.5,
          borderBottom: '1px solid #f0d5d1',
          backgroundColor: highlighted
            ? '#fff4e8'
            : index % 2 === 0
              ? '#fff'
              : '#f7f9fc',
          transition: 'background-color 0.4s ease',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.25 }}
        >
          <Box
            sx={{
              minWidth: 28,
              height: 28,
              px: 0.6,
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center',
              backgroundColor: '#2F3542',
              color: '#fff',
              fontWeight: 700,
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {row.sr || '-'}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {fieldInput('particulars', {
              ...lockedFieldStyle,
              fontWeight: 600,
            })}
          </Box>
          {onDelete ? (
            <IconButton
              size='small'
              aria-label='Delete item'
              onClick={() => onDelete(row)}
              sx={{ color: '#be210b', mt: -0.4 }}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          ) : null}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 1.25,
          }}
        >
          <MobileField label='Dimensions' full>
            {fieldInput('dimensions')}
          </MobileField>
          <MobileField label='Size'>
            <Box
              sx={{
                height: 42,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                borderRadius: '8px',
                border: '1px solid #e4e7ec',
                backgroundColor: '#f7f9fc',
                fontSize: '1rem',
                lineHeight: 1,
              }}
            >
              {saving
                ? 'Saving...'
                : row.size === '' || row.size === null
                  ? '-'
                  : row.size}
            </Box>
          </MobileField>
          <MobileField label='Unit'>{unitField}</MobileField>
          <MobileField label='Quantity'>{fieldInput('quantity')}</MobileField>
          <MobileField label='Status'>{statusField}</MobileField>
          <MobileField label='Remarks' full>
            {remarksField}
          </MobileField>
        </Box>
      </Box>
    );
  }

  return (
    <TableRow
      id={`element-item-${row.id}`}
      sx={{
        backgroundColor: highlighted ? '#fff4e8' : '#fff',
        transition: 'background-color 0.4s ease',
        '&:hover': { backgroundColor: highlighted ? '#fff4e8' : '#fff8f7' },
        '& td': { borderBottom: '1px solid #f3e8e6' },
      }}
    >
      <TableCell sx={desktopCellSx}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '6px',
            display: 'grid',
            placeItems: 'center',
            backgroundColor: '#fff1ef',
            color: '#be210b',
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {row.sr || '-'}
        </Box>
      </TableCell>
      <TableCell
        sx={{
          ...desktopCellSx,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          fontWeight: 600,
          color: '#2F3542',
        }}
      >
        {fieldInput('particulars')}
      </TableCell>
      <TableCell sx={desktopCellSx}>
        {fieldInput('dimensions', dimensionDesktopStyle)}
      </TableCell>
      <TableCell sx={{ ...desktopCellSx, color: '#667085', fontWeight: 600 }}>
        {saving ? '...' : row.size === '' || row.size === null ? '-' : row.size}
      </TableCell>
      <TableCell sx={desktopCellSx}>{unitField}</TableCell>
      <TableCell sx={desktopCellSx}>
        {fieldInput('quantity', quantityDesktopStyle)}
      </TableCell>
      <TableCell sx={desktopCellSx}>{statusField}</TableCell>
      <TableCell sx={desktopCellSx}>{remarksField}</TableCell>
      {showUpdatedBy ? (
        <TableCell
          sx={{
            ...desktopCellSx,
            overflow: 'visible',
            color: '#2F3542',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            fontWeight: 600,
          }}
        >
          {row.statusUpdatedBy || '-'}
        </TableCell>
      ) : null}
      {onDelete ? (
        <TableCell sx={{ ...desktopCellSx, overflow: 'visible', textAlign: 'center' }}>
          <IconButton
            size='small'
            aria-label='Delete item'
            onClick={() => onDelete(row)}
            sx={{ color: '#be210b' }}
          >
            <DeleteOutlineIcon fontSize='small' />
          </IconButton>
        </TableCell>
      ) : null}
    </TableRow>
  );
});

const pageTheme = createTheme({
  typography: {
    fontFamily: 'Roboto, sans-serif',
    body1: { fontSize: '0.875rem' },
    body2: { fontSize: '0.875rem' },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '0.675rem',
        },
        head: {
          fontSize: '0.7rem',
          fontWeight: 700,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily: 'Roboto, sans-serif' },
        input: { fontFamily: 'Roboto, sans-serif', fontSize: '1rem' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        label: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '0.75rem',
          fontWeight: 500,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Roboto, sans-serif',
          fontSize: '1.3rem',
          fontWeight: 500,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: { fontFamily: 'Roboto, sans-serif', fontSize: '0.85rem' },
      },
    },
  },
});

const dialogPaperSx = {
  borderRadius: '16px',
  width: 440,
  maxWidth: 'calc(100% - 32px)',
  boxShadow: '0 24px 48px rgba(16, 24, 40, 0.18)',
  overflow: 'hidden',
};

const dialogHeaderSx = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  px: 3,
  pt: 2.5,
  justifyContent: 'center',
  alignItems: 'center',
};

const dialogTitleSx = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#101828',
  lineHeight: 1.3,
};

const dialogHintSx = {
  mt: 0.5,
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.85rem',
  color: '#667085',
  lineHeight: 1.5,
};

const dialogCloseSx = {
  color: '#98a2b3',
  mt: -0.25,
  '&:hover': { backgroundColor: '#f2f4f7', color: '#344054' },
};

const dangerIconSx = {
  width: 44,
  height: 44,
  borderRadius: '12px',
  backgroundColor: '#fef3f2',
  color: '#b42318',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const addIconSx = {
  ...dangerIconSx,
  backgroundColor: '#fff4f2',
  color: '#be210b',
};

const itemNameCardSx = {
  border: '1px solid #fecdca',
  backgroundColor: '#fffbfa',
  borderRadius: '10px',
  px: 1.5,
  py: 1.25,
};

const itemNameLabelSx = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.7rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: '#b42318',
  textTransform: 'uppercase',
  mb: 0.25,
};

const itemNameValueSx = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#101828',
  lineHeight: 1.4,
  wordBreak: 'break-word',
};

const sectionChipSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 2,
  px: 1.25,
  py: 1,
  borderRadius: '10px',
  backgroundColor: '#fafafa',
  border: '1px solid #eaecf0',
  minWidth: 0,
};

const sectionCodeSx = {
  flexShrink: 0,
  px: 0.75,
  py: 0.25,
  borderRadius: '6px',
  backgroundColor: '#fff4f2',
  color: '#be210b',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 700,
  lineHeight: 1.4,
};

const sectionTitleSx = {
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#344054',
  lineHeight: 1.35,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const addItemFieldSx = {
  '& .MuiInputLabel-root': {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.9rem',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#be210b' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.95rem',
    '& fieldset': { borderColor: '#e4e7ec' },
    '&:hover fieldset': { borderColor: '#be210b' },
    '&.Mui-focused fieldset': { borderColor: '#be210b', borderWidth: 1.5 },
  },
};

const dialogActionsSx = {
  px: 3,
  pt: 2,
  pb: 2.5,
  gap: 1,
};

const cancelButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  color: '#344054',
  border: '1px solid #d0d5dd',
  px: 2,
  '&:hover': { backgroundColor: '#f9fafb', borderColor: '#d0d5dd' },
};

const dangerButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  px: 2.25,
  boxShadow: 'none',
  backgroundColor: '#b42318',
  '&:hover': { backgroundColor: '#912018', boxShadow: 'none' },
};

const primaryButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '8px',
  px: 2.25,
  boxShadow: 'none',
  backgroundColor: '#be210b',
  '&:hover': { backgroundColor: '#a61c09', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#f2b8b0', color: '#fff' },
};

const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#fff',
    fontFamily: 'Roboto, sans-serif',
    '& fieldset': { borderColor: '#efc4bd' },
    '&:hover fieldset': { borderColor: '#be210b' },
    '&.Mui-focused fieldset': { borderColor: '#be210b', borderWidth: 1.5 },
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '0.9rem',
  },
};

const desktopCellSx = {
  px: 0.75,
  py: 0.75,
  verticalAlign: 'middle',
  minWidth: 0,
  overflow: 'hidden',
};

const desktopInputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #e6e8ec',
  borderRadius: 6,
  padding: '4px 8px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#2F3542',
  background: '#fff',
  height: 28,
  minHeight: 28,
};

const dimensionDesktopStyle = {
  ...desktopInputStyle,
  width: '100%',
  maxWidth: '100%',
  height: 26,
  minHeight: 26,
  padding: '2px 6px',
  fontSize: '0.72rem',
};

const quantityDesktopStyle = {
  ...dimensionDesktopStyle,
  textAlign: 'center',
};

const remarksDesktopStyle = {
  ...desktopInputStyle,
  width: '100%',
  height: 'auto',
  minHeight: 24,
  padding: '6px 8px',
  fontSize: '0.75rem',
  lineHeight: 1.35,
  resize: 'none',
  whiteSpace: 'pre-wrap',
  overflow: 'auto',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #e4e7ec',
  borderRadius: 6,
  padding: '6px 8px',
  fontFamily: 'Roboto, sans-serif',
  fontSize: '0.675rem',
  fontWeight: 400,
  background: '#fff',
};

const mobileInputStyle = {
  ...inputStyle,
  fontSize: '1rem',
  padding: '10px 12px',
  minHeight: 42,
};

const UNIT_OPTIONS = ['Sq.ft', 'Sq.mtr', 'Rft', 'Cft', 'Nos', 'Feet', 'Metre', 'Inch'];

const UnitSelect = ({ value, disabled, mobile, onChange }) => {
  const current = value || '';
  const options =
    !current || UNIT_OPTIONS.includes(current) ? UNIT_OPTIONS : [current, ...UNIT_OPTIONS];

  return (
    <Select
      value={current}
      disabled={disabled}
      displayEmpty
      onChange={onChange}
      IconComponent={ExpandMoreIcon}
      renderValue={(selected) => selected || 'Select'}
      MenuProps={{
        PaperProps: {
          sx: {
            mt: 0.5,
            borderRadius: '10px',
            border: '1px solid #eaecf0',
            boxShadow: '0 12px 28px rgba(16, 24, 40, 0.14)',
          },
        },
      }}
      sx={{
        width: '100%',
        height: mobile ? 42 : 28,
        borderRadius: mobile ? '8px' : '6px',
        backgroundColor: '#fff',
        color: current ? '#2F3542' : '#667085',
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 500,
        fontSize: mobile ? '1rem' : '0.75rem',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e8ec' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#be210b' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#be210b',
          borderWidth: 1.5,
        },
        '& .MuiSelect-select': {
          height: mobile ? '42px !important' : '28px !important',
          minHeight: 'unset !important',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          padding: mobile ? '0 32px 0 12px !important' : '0 22px 0 8px !important',
          fontSize: mobile ? '1rem' : '0.75rem',
        },
        '& .MuiSelect-icon': {
          color: '#667085',
          right: 2,
          fontSize: mobile ? 22 : 16,
        },
      }}
    >
      <MenuItem value=''>Select</MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option}
          value={option}
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: mobile ? '0.95rem' : '0.8rem',
            fontWeight: 500,
          }}
        >
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

const statusTone = {
  verified: {
    bg: '#ecfdf3',
    color: '#067647',
    border: '#6ce9a6',
    accent: '#12b76a',
  },
  discrepancy: {
    bg: '#fef3f2',
    color: '#b42318',
    border: '#fda29b',
    accent: '#f04438',
  },
  exceeded: {
    bg: '#fffaeb',
    color: '#b54708',
    border: '#fec84b',
    accent: '#f79009',
  },
};

const statusChoices = [
  { value: 'verified', label: 'Verified' },
  { value: 'discrepancy', label: 'Discrepancy' },
  { value: 'exceeded', label: 'Exceeded' },
];

const StatusSelect = ({ value, disabled, mobile, onChange }) => {
  const tone = statusTone[value] || {
    bg: '#fff',
    color: '#667085',
    border: '#e6e8ec',
    accent: '#d0d5dd',
  };

  return (
    <Select
      value={value}
      disabled={disabled}
      displayEmpty
      onChange={onChange}
      IconComponent={ExpandMoreIcon}
      renderValue={(selected) =>
        statusChoices.find((choice) => choice.value === selected)?.label ||
        'Select'
      }
      MenuProps={{
        PaperProps: {
          sx: {
            mt: 0.5,
            borderRadius: '10px',
            border: '1px solid #eaecf0',
            boxShadow: '0 12px 28px rgba(16, 24, 40, 0.14)',
          },
        },
      }}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: mobile ? 42 : 28,
        borderRadius: mobile ? '8px' : '6px',
        backgroundColor: tone?.bg,
        color: tone?.color,
        fontFamily: 'Roboto, sans-serif',
        fontWeight: 600,
        fontSize:'0.75rem',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: tone?.border },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: tone?.accent,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: tone?.accent,
          borderWidth: 1.5,
        },
        '& .MuiSelect-select': {
          height: mobile ? '42px !important' : '28px !important',
          minHeight: 'unset !important',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          padding: mobile
            ? '0 36px 0 14px !important'
            : '0 22px 0 8px !important',
          fontSize: mobile ? '0.6rem' : '0.75rem',
        },
        '& .MuiSelect-icon': {
          color: tone?.color,
          right: mobile ? 8 : 2,
          fontSize: mobile ? 22 : 16,
        },
      }}
    >
      {statusChoices.map((choice) => {
        const choiceTone = statusTone[choice.value];
        return (
          <MenuItem
            key={choice.value || 'empty'}
            value={choice.value}
            sx={{
              // mx: 0.75,
              // my: 0.35,
              borderRadius: '8px',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 600,
              fontSize: mobile ? '0.7rem' : '0.7rem',
              color: choice.value ? choiceTone.color : '#667085',
              '&.Mui-selected': {
                backgroundColor: choice.value ? choiceTone.bg : '#f2f4f7',
                color: choice.value ? choiceTone.color : '#344054',
              },
              '&.Mui-selected:hover': {
                backgroundColor: choice.value ? choiceTone.bg : '#f2f4f7',
              },
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                mr: 1,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: choice.value ? choiceTone.accent : '#d0d5dd',
              }}
            />
            {choice.label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

const MobileField = ({ label, full, compact, children }) => (
  <Box sx={{ gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
    <Typography
      component='label'
      sx={{
        display: 'block',
        mb: compact ? 0.25 : 0.4,
        fontSize: compact ? '0.65rem' : '0.72rem',
        fontWeight: 600,
        color: '#6b7280',
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const readStoredList = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch (error) {
    return [];
  }
};

const SharePill = ({ label, percent, color, background, border, track }) => (
  <Box
    sx={{
      width: { xs: label ? 98 : 64, md: label ? 118 : 72 },
      px: 0.75,
      py: 0.6,
      borderRadius: '10px',
      backgroundColor: background,
      border: `1px solid ${border}`,
      flex: { xs: '1 1 auto', md: '0 0 auto' },
      minWidth: { xs: 88, md: 'auto' },
      maxWidth: { xs: '32%', md: 'none' },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.45 }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
      {label ? (
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color,
            lineHeight: 1,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Typography>
      ) : (
        <Box sx={{ flex: 1 }} />
      )}
      <Typography sx={{ fontSize: 11, fontWeight: 800, color, lineHeight: 1, flexShrink: 0 }}>
        {percent}%
      </Typography>
    </Box>
    <Box
      sx={{
        width: '100%',
        height: 6,
        borderRadius: 99,
        backgroundColor: track,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: `${Math.min(100, Math.max(percent, 0))}%`,
          minWidth: percent > 0 ? 6 : 0,
          height: '100%',
          borderRadius: 99,
          backgroundColor: color,
        }}
      />
    </Box>
  </Box>
);

const LocationStructure = () => {
  const structureVerifier = readStoredList('roles').includes('ZONE HEAD');
  const assignedLocation = localStorage.getItem('location') || '';
  const assignedZone = (() => {
    const raw = localStorage.getItem('zones') || '';
    if (!raw) return '';
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const first = parsed[0];
        return typeof first === 'string'
          ? first
          : first?.zone || first?.location || '';
      }
      if (parsed && typeof parsed === 'object') {
        return parsed.zone || parsed.location || '';
      }
      return String(parsed || '');
    } catch (error) {
      return raw;
    }
  })();
  const isMobile = useMediaQuery('(max-width:799px)', { noSsr: true });
  const [sheet, setSheet] = useState({ sections: [], total: 0 });
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [expandedSection, setExpandedSection] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [addItem, setAddItem] = useState(null);
  const [newParticulars, setNewParticulars] = useState('');
  const [adding, setAdding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [focusItemId, setFocusItemId] = useState(null);
  const savedValues = useRef({});

  useEffect(() => {
    if (focusItemId == null) return undefined;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`element-item-${focusItemId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const clear = window.setTimeout(() => setFocusItemId(null), 1600);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [focusItemId, sheet]);

  const rememberSaved = (sections) => {
    const next = {};
    sections.forEach((section) => {
      section.rows.forEach((row) => {
        next[row.id] = {
          particulars: String(row.particulars ?? ''),
          dimensions: String(row.dimensions ?? ''),
          quantity: String(row.quantity ?? ''),
          unit: String(row.unit ?? ''),
          status: String(row.status ?? ''),
          remarks: String(row.remarks ?? ''),
        };
      });
    });
    savedValues.current = next;
  };

  const fetchLocations = useCallback(async () => {
    const eventId = localStorage.getItem('event_id');
    if (!eventId) {
      setLocations([]);
      setSelectedLocation('');
      return [];
    }

    const response = await elementItemService.getElementLocations(eventId);
    const list = response.data?.locations || [];
    const firstLocation = list[0]?.location || '';
    setLocations(list);
    setSelectedLocation((current) =>
      current && list.some((item) => item.location === current)
        ? current
        : firstLocation,
    );
    return list;
  }, []);

  const fetchItems = useCallback(
    async (location, options = {}) => {
      const eventId = localStorage.getItem('event_id');
      if (!eventId || (!location && !(structureVerifier && assignedZone))) {
        setSheet({ sections: [], total: 0 });
        setLoading(false);
        return null;
      }

      if (!options.silent) setLoading(true);
      try {
        const response = await elementItemService.getElementItemsByEvent(
          eventId,
          location,
          '',
          structureVerifier ? assignedZone : '',
        );
        const grouped = elementItemService.groupElementItems(response.data);
        rememberSaved(grouped.sections);
        setSheet(grouped);
        if (!options.keepSection) setExpandedSection('');
        return grouped;
      } catch (error) {
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Could not load element items',
        );
        return null;
      } finally {
        if (!options.silent) setLoading(false);
      }
    },
    [assignedZone, structureVerifier],
  );

  useEffect(() => {
    if (structureVerifier) {
      setLocations(assignedLocation ? [{ location: assignedLocation }] : []);
      setSelectedLocation(assignedLocation);
      if (!assignedLocation && !assignedZone) setLoading(false);
      return;
    }

    const loadLocations = async () => {
      try {
        const list = await fetchLocations();
        if (!list.length) setLoading(false);
      } catch (error) {
        setLoading(false);
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Could not load locations',
        );
      }
    };
    loadLocations();
  }, [assignedLocation, assignedZone, fetchLocations, structureVerifier]);

  const activeLocationName = selectedLocation || locations[0]?.location || '';

  useEffect(() => {
    if (!selectedLocation && locations[0]?.location) {
      setSelectedLocation(locations[0].location);
    }
  }, [locations, selectedLocation]);

  useEffect(() => {
    if (structureVerifier) {
      if (assignedZone || activeLocationName) fetchItems(activeLocationName);
      return;
    }
    if (activeLocationName) fetchItems(activeLocationName);
  }, [activeLocationName, assignedZone, fetchItems, structureVerifier]);

  const uploadFile = async (file) => {
    if (!file || uploading) return;

    setUploading(true);
    try {
      await elementItemService.uploadElementItems(file);
      toast.success(`${file.name} uploaded`);
      setSearch('');
      const list = await fetchLocations();
      const nextLocation =
        list.find((item) => item.location === selectedLocation)?.location ||
        list[0]?.location ||
        '';
      if (nextLocation === selectedLocation) {
        await fetchItems(nextLocation);
      } else {
        setSelectedLocation(nextLocation);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Excel upload failed',
      );
    } finally {
      setUploading(false);
    }
  };

  const requestUpload = (file) => {
    if (!file || uploading) return;
    if (rowCount > 0) {
      setConfirm({ type: 'replace', file });
      return;
    }
    uploadFile(file);
  };

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    requestUpload(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    requestUpload(event.dataTransfer.files?.[0]);
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    if (confirm.type === 'replace') {
      const file = confirm.file;
      setConfirm(null);
      uploadFile(file);
      return;
    }

    if (confirm.type === 'delete') {
      setClearing(true);
      try {
        await elementItemService.deleteElementItem(confirm.row.id);
        const deletedName = confirm.row?.particulars;
        setConfirm(null);
        showActionSuccess('DELETED', deletedName);
        await fetchItems(activeLocationName || assignedLocation);
      } catch (error) {
        toast.error(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            'Could not delete this item',
        );
      } finally {
        setClearing(false);
      }
      return;
    }

    const eventId = localStorage.getItem('event_id');
    setClearing(true);
    try {
      await elementItemService.clearElementItems(eventId);
      setConfirm(null);
      toast.success('Element items cleared');
      const list = await fetchLocations();
      const nextLocation =
        list.find((item) => item.location === selectedLocation)?.location ||
        list[0]?.location ||
        '';
      if (!nextLocation) {
        setSelectedLocation('');
        setSheet({ sections: [], total: 0 });
      } else if (nextLocation === selectedLocation) {
        await fetchItems(nextLocation);
      } else {
        setSelectedLocation(nextLocation);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Could not clear the element items',
      );
    } finally {
      setClearing(false);
    }
  };

  const openAddItem = (section) => {
    setNewParticulars('');
    setAddItem({ key: section.key, code: section.code, title: section.title });
  };

  const downloadReport = async () => {
    if (structureVerifier) return;
    const eventId = localStorage.getItem('event_id');
    if (!eventId || downloading) return;
    setDownloading(true);
    try {
      const [itemsResponse, eventResponse] = await Promise.all([
        elementItemService.getElementItemsByEvent(
          eventId,
          '',
          '',
          structureVerifier ? assignedZone : '',
        ),
        eventService.getEvent(eventId).catch(() => null),
      ]);
      const locations = elementItemService.locationsFromPayload(itemsResponse.data);
      if (!locations.length) {
        toast.error('No element items to download');
        return;
      }
      const event = eventResponse?.data || {};
      const eventName = event.name || event.event_name ;
      const fileName = `${eventName ? `${String(eventName).replace(/[\\/?*[\]:]/g, ' ').trim()}-ELEMENT REPORT` : 'ELEMENT REPORT'}.xlsx`;
      downloadElementReport({
        eventName,
        locations,
        fileName,
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Could not download the report',
      );
    } finally {
      setDownloading(false);
    }
  };

  const handleAddItem = async () => {
    const particulars = newParticulars.trim().toUpperCase();
    const location = activeLocationName || assignedLocation || assignedZone;
    if (!addItem || !particulars || !location) return;
    const eventId = parseInt(localStorage.getItem('event_id'), 10);
    const previousIds = new Set(
      (sheet.sections || []).flatMap((section) => section.rows.map((row) => row.id)),
    );
    setAdding(true);
    try {
      const created = await elementItemService.createElementItem({
        event_id: eventId,
        location,
        section_code: addItem.code,
        section_name: addItem.title,
        particulars,
      });
      const createdId = created?.data?.id ?? created?.data?.item?.id;
      const sectionKey = addItem.key;
      setAddItem(null);
      setNewParticulars('');
      setExpandedSection(sectionKey);
      showActionSuccess('ADDED', particulars);
      const grouped = await fetchItems(activeLocationName || assignedLocation, {
        silent: true,
        keepSection: true,
      });
      const section =
        grouped?.sections?.find((item) => item.key === sectionKey) ||
        grouped?.sections?.find(
          (item) => item.code === addItem.code && item.title === addItem.title,
        );
      if (section?.key) setExpandedSection(section.key);
      const addedRow =
        section?.rows?.find((row) => String(row.id) === String(createdId)) ||
        [...(section?.rows || [])]
          .reverse()
          .find((row) => !previousIds.has(row.id) && row.particulars === particulars) ||
        [...(section?.rows || [])].reverse().find((row) => row.particulars === particulars);
      if (addedRow?.id != null) setFocusItemId(addedRow.id);
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Could not add this item',
      );
    } finally {
      setAdding(false);
    }
  };

  const saveRow = useCallback(async (row, draft) => {
    const payload = {
      particulars: String(draft.particulars ?? '').toUpperCase(),
      dimensions: String(draft.dimensions ?? ''),
      quantity: String(draft.quantity ?? ''),
      unit: String(draft.unit ?? ''),
    };
    const previous = savedValues.current[row.id] || {};
    if (
      payload.particulars === String(previous.particulars ?? '') &&
      payload.dimensions === String(previous.dimensions ?? '') &&
      payload.quantity === String(previous.quantity ?? '') &&
      payload.unit === String(previous.unit ?? '')
    ) {
      return;
    }

    const quantityNumber = Number(payload.quantity);
    const quantity =
      payload.quantity === ''
        ? ''
        : Number.isFinite(quantityNumber)
          ? quantityNumber
          : payload.quantity;

    setSavingId(row.id);
    try {
      const response = await elementItemService.updateElementItem(row.id, {
        particulars: payload.particulars,
        dimensions: payload.dimensions,
        quantity,
        unit: payload.unit,
      });
      const saved = response.data?.data || response.data || {};
      setSheet((current) => ({
        ...current,
        sections: current.sections.map((section) => ({
          ...section,
          rows: section.rows.map((item) =>
            item.id === row.id
              ? {
                  ...item,
                  particulars: payload.particulars,
                  dimensions: payload.dimensions,
                  quantity: payload.quantity,
                  unit: payload.unit,
                  size:
                    saved.size !== undefined && saved.size !== null
                      ? saved.size
                      : item.size,
                }
              : item,
          ),
        })),
      }));
      savedValues.current[row.id] = {
        ...(savedValues.current[row.id] || {}),
        ...payload,
      };
      const changes = [
        payload.particulars !== String(previous.particulars ?? row.particulars ?? '')
          ? {
              label: 'Item name',
              from: displayValue(previous.particulars ?? row.particulars),
              to: displayValue(payload.particulars),
            }
          : null,
        payload.dimensions !== String(previous.dimensions ?? row.dimensions ?? '')
          ? {
              label: 'Dimensions',
              from: displayValue(previous.dimensions ?? row.dimensions),
              to: displayValue(payload.dimensions),
            }
          : null,
        payload.quantity !== String(previous.quantity ?? row.quantity ?? '')
          ? {
              label: 'Quantity',
              from: displayValue(previous.quantity ?? row.quantity),
              to: displayValue(payload.quantity),
            }
          : null,
        payload.unit !== String(previous.unit ?? row.unit ?? '')
          ? {
              label: 'Unit',
              from: displayValue(previous.unit ?? row.unit),
              to: displayValue(payload.unit),
            }
          : null,
      ].filter(Boolean);
      showEditSuccess(row.particulars || 'Item', changes);
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Could not save this row',
      );
    } finally {
      setSavingId(null);
    }
  }, []);

  const saveReview = useCallback(async (row, review) => {
    const previous = savedValues.current[row.id] || {};
    if (
      String(review.status || '') ===
        String(previous.status ?? row.status ?? '') &&
      String(review.remarks || '') ===
        String(previous.remarks ?? row.remarks ?? '')
    ) {
      return;
    }

    setSavingId(row.id);
    try {
      const payload = {
        status: review.status,
        remarks: review.remarks,
      };
      if (
        review.status === 'verified' ||
        review.status === 'discrepancy' ||
        review.status === 'exceeded'
      ) {
        payload.status_updated_by = localStorage.getItem('user_name') || '';
      }
      await elementItemService.updateElementItem(row.id, payload);
      setSheet((current) => ({
        ...current,
        sections: current.sections.map((section) => ({
          ...section,
          rows: section.rows.map((item) =>
            item.id === row.id
              ? {
                  ...item,
                  status: review.status,
                  remarks: review.remarks,
                  statusUpdatedBy:
                    payload.status_updated_by || item.statusUpdatedBy,
                }
              : item,
          ),
        })),
      }));
      savedValues.current[row.id] = {
        ...(savedValues.current[row.id] || {}),
        status: review.status,
        remarks: review.remarks,
      };
      const changes = [
        String(review.status || '') !== String(previous.status ?? row.status ?? '')
          ? {
              label: 'Status',
              from: statusLabel(previous.status ?? row.status),
              to: statusLabel(review.status),
            }
          : null,
        String(review.remarks || '') !== String(previous.remarks ?? row.remarks ?? '')
          ? {
              label: 'Remarks',
              from: displayValue(previous.remarks ?? row.remarks),
              to: displayValue(review.remarks),
            }
          : null,
      ].filter(Boolean);
      showEditSuccess(row.particulars || 'Item', changes);
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          'Could not save this row',
      );
    } finally {
      setSavingId(null);
    }
  }, []);

  const sectionCount = sheet?.sections?.length || 0;
  const rowCount = sheet?.total || 0;
  const sections = useMemo(() => {
    const groups = sheet?.sections || [];
    const query = search.trim().toLowerCase();
    if (!query) return groups;

    return groups
      .filter((group) => {
        const titleMatch = `${group.code} ${group.title}`
          .toLowerCase()
          .includes(query);
        const rowMatch = group.rows.some((row) =>
          [
            row.sr,
            row.particulars,
            row.dimensions,
            row.quantity,
            row.unit,
            row.size,
          ]
            .join(' ')
            .toLowerCase()
            .includes(query),
        );
        return titleMatch || rowMatch;
      })
      .map((group) => {
        if (`${group.code} ${group.title}`.toLowerCase().includes(query))
          return group;
        return {
          ...group,
          rows: group.rows.filter((row) =>
            [
              row.sr,
              row.particulars,
              row.dimensions,
              row.quantity,
              row.unit,
              row.size,
            ]
              .join(' ')
              .toLowerCase()
              .includes(query),
          ),
        };
      });
  }, [sheet, search]);

  const pageBody = (
    <ThemeProvider theme={pageTheme}>
      <div
        className='page-content-wrapper scrollable'
        style={{
          width: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          padding: isMobile ? '8px' : '12px 16px',
        }}
      >
        {!structureVerifier && (
          <Paper
            elevation={0}
            sx={{
              m: { xs: 0.5, md: 1 },
              p: { xs: 1.25, md: 2 },
              borderRadius: 3,
              border: '1px solid #f3d4cf',
              background: 'linear-gradient(180deg, #ffffff 0%, #fff7f6 100%)',
            }}
          >
            <Box
              onDragOver={(event) => {
                event.preventDefault();
                if (!uploading) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                px: { xs: 2, sm: 3 },
                py: 3,
                borderRadius: 2.5,
                border: '1.5px dashed',
                borderColor: dragOver ? '#be210b' : '#efc4bd',
                backgroundColor: dragOver ? '#fff1ef' : '#fff',
                transition: 'border-color 0.2s, background-color 0.2s',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    color: '#be210b',
                    backgroundColor: '#fff1ef',
                  }}
                >
                  {uploading ? (
                    <CircularProgress size={26} sx={{ color: '#be210b' }} />
                  ) : (
                    <FaFileExcel size={28} color='#217346' />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, color: '#2F3542' }}
                  >
                    {uploading
                      ? 'Uploading element sheet'
                      : 'Upload element sheet'}
                  </Typography>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mt: 0.4 }}
                  >
                    {rowCount
                      ? 'Drop a new Excel file here to replace the current sheet.'
                      : 'Drop an Excel file here, or choose one from your computer. Each sheet tab becomes a location.'}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.8,
                      mt: 1.2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {['.xlsx', '.xls', '.csv'].map((type) => (
                      <Chip
                        key={type}
                        label={type}
                        size='small'
                        sx={{
                          height: 22,
                          color: '#be210b',
                          backgroundColor: '#fff1ef',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'stretch',
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0,
                }}
              >
                <Button
                  variant='contained'
                  component='label'
                  fullWidth={isMobile}
                  disabled={uploading || clearing}
                  startIcon={!uploading && <MdOutlineFileUpload size={18} />}
                  sx={{
                    backgroundColor: '#be210b',
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2.2,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#a61c09',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {uploading
                    ? 'Uploading...'
                    : rowCount
                      ? 'REPLACE FILE'
                      : 'CHOOSE FILE'}
                  <input
                    hidden
                    type='file'
                    accept='.xlsx,.xls,.csv'
                    onChange={handleUpload}
                  />
                </Button>
                {rowCount > 0 && (
                  <Button
                    variant='outlined'
                    fullWidth={isMobile}
                    disabled={uploading || clearing}
                    onClick={() => setConfirm({ type: 'clear' })}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      color: '#be210b',
                      borderColor: '#efc4bd',
                      '&:hover': {
                        borderColor: '#be210b',
                        backgroundColor: '#fff1ef',
                      },
                    }}
                  >
                    CLEAR
                  </Button>
                )}
              </Box>
            </Box>
            {rowCount > 0 && (
              <Box
                sx={{
                  mt: 1.5,
                  px: 1.5,
                  py: 1.2,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  flexWrap: 'wrap',
                  backgroundColor: '#fff',
                  border: '1px solid #f3d4cf',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    minWidth: 0,
                  }}
                >
                  <FaFileExcel color='#217346' size={18} />
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, color: '#2F3542' }}
                  >
                    Element items for this structures
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size='small'
                    label={`${sectionCount} Structure${sectionCount === 1 ? '' : 's'}`}
                  />
                  {/* <Chip size='small' label={`${rowCount} rows`} /> */}
                </Box>
              </Box>
            )}
          </Paper>
        )}

        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            m: { xs: 0.5, md: 1 },
            overflow: 'hidden',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          <>
            <Box
              sx={{
                m: { xs: 1, md: 2 },
                mb: 1.5,
                p: { xs: 1.1, md: 1.5 },
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                borderRadius: 2.5,
                border: '1px solid #f3d4cf',
                background: 'linear-gradient(180deg, #fff7f6 0%, #ffffff 100%)',
              }}
            >
              {!structureVerifier && (
                <Box
                  sx={{ flex: '1 1 240px', minWidth: { xs: '100%', md: 0 } }}
                >
                  <Typography
                    variant='body2'
                    sx={{ mb: 0.7, fontWeight: 600, color: '#2F3542' }}
                  >
                    ZONES
                  </Typography>
                  <FormControl fullWidth sx={filterFieldSx}>
                    <Select
                      value={activeLocationName}
                      displayEmpty
                      disabled={structureVerifier}
                      onChange={(event) => {
                        setSearch('');
                        setExpandedSection('');
                        setSelectedLocation(event.target.value);
                      }}
                      startAdornment={
                        <InputAdornment position='start' sx={{ ml: 0.4 }}>
                          <MdLocationCity color='#be210b' size={18} />
                        </InputAdornment>
                      }
                      renderValue={(value) => value || 'Select a location'}
                    >
                      {locations.length === 0 && (
                        <MenuItem value='' disabled>
                          No locations
                        </MenuItem>
                      )}
                      {locations.map((item) => (
                        <MenuItem key={item.location} value={item.location}>
                          {item.location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              {structureVerifier && (
                <Box sx={{ flex: '1 1 100%' }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, color: '#2F3542' }}
                  >
                    {assignedZone || assignedLocation || 'No zone assigned'}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  flex: structureVerifier ? '1 1 100%' : '1.4 1 280px',
                  minWidth: {
                    xs: '100%',
                    md: structureVerifier ? '100%' : 0,
                  },
                }}
              >
                <Typography
                  variant='body2'
                  sx={{ mb: 0.7, fontWeight: 600, color: '#2F3542' }}
                >
                  SEARCH
                </Typography>
                <TextField
                  fullWidth
                  placeholder='SEARCH PARTICULRS'
                  value={search?.toUpperCase()}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setExpandedSection('');
                  }}
                  sx={filterFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon sx={{ color: '#be210b', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          aria-label='Clear search'
                          onClick={() => {
                            setSearch('');
                            setExpandedSection('');
                          }}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
              {!structureVerifier && (
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '0 0 auto' },
                  ml: { md: 'auto' },
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <Button
                  variant='outlined'
                  fullWidth={isMobile}
                  disabled={downloading}
                  onClick={downloadReport}
                  startIcon={
                    downloading ? (
                      <CircularProgress size={16} sx={{ color: '#217346' }} />
                    ) : (
                      <FaFileExcel color='#217346' size={16} />
                    )
                  }
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    fontWeight: 600,
                    color: '#217346',
                    borderColor: '#b7e4c7',
                    backgroundColor: '#fff',
                    px: 2,
                    '&:hover': {
                      borderColor: '#217346',
                      backgroundColor: '#f3faf6',
                    },
                  }}
                >
                  {downloading ? 'DOWNLOADING...' : 'DOWNLOAD REPORT'}
                </Button>
              </Box>
              )}
            </Box>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: '#be210b' }} />
              </Box>
            ) : rowCount === 0 ? (
              <Typography
                variant='body2'
                sx={{ px: 3, pb: 3, color: 'text.secondary' }}
              >
                {structureVerifier && !assignedZone
                  ? 'No zone is assigned to this user.'
                  : structureVerifier
                    ? 'No structures are assigned to this zone.'
                    : 'No element items for this location yet. Upload an Excel sheet to add them.'}
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    px: { xs: 1, md: 2 },
                    pb: { xs: 1, md: 2 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  {sections.map((section) => {
                    const lineRows = section.rows;
                    const verifiedCount = lineRows.filter((row) => row.status === 'verified').length;
                    const discrepancyCount = lineRows.filter(
                      (row) => row.status === 'discrepancy',
                    ).length;
                    const exceededCount = lineRows.filter(
                      (row) => row.status === 'exceeded',
                    ).length;
                    const share = (count) =>
                      lineRows.length ? Math.round((count / lineRows.length) * 100) : 0;
                    return (
                      <Accordion
                        key={section.key}
                        disableGutters
                        expanded={expandedSection === section.key}
                        onChange={(_, isExpanded) =>
                          setExpandedSection(isExpanded ? section.key : '')
                        }
                        TransitionProps={{ unmountOnExit: true }}
                        sx={{
                          width: '100%',
                          maxWidth: '100%',
                          minWidth: 0,
                          border: '1px solid #f0d5d1',
                          borderRadius: '10px !important',
                          overflow: 'hidden',
                          '&:before': { display: 'none' },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ backgroundColor: '#fff7f6' }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 1,
                              width: '100%',
                              pr: 1,
                            }}
                          >
                            {section.code && (
                              <Box
                                sx={{
                                  minWidth: 28,
                                  height: 28,
                                  px: 0.8,
                                  borderRadius: 1,
                                  display: 'grid',
                                  placeItems: 'center',
                                  backgroundColor: '#be210b',
                                  color: '#fff',
                                  fontWeight: 700,
                                  fontSize: 13,
                                }}
                              >
                                {section.code}
                              </Box>
                            )}
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight: 600,
                                color: '#2F3542',
                                flex: '1 1 120px',
                                minWidth: 0,
                              }}
                            >
                              {section.title}
                            </Typography>
                            {!structureVerifier && (
                            <Box
                              sx={{
                                ml: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                flexWrap: 'wrap',
                              }}
                            >
                              <SharePill
                                label='Verified'
                                percent={share(verifiedCount)}
                                color='#067647'
                                background='#ecfdf3'
                                border='#abefc6'
                                track='#d1fadf'
                              />
                              <SharePill
                                label='Discrepancy'
                                percent={share(discrepancyCount)}
                                color='#b42318'
                                background='#fef3f2'
                                border='#fecdca'
                                track='#fee4e2'
                              />
                              <SharePill
                                label='Exceeded'
                                percent={share(exceededCount)}
                                color='#b54708'
                                background='#fffaeb'
                                border='#fedf89'
                                track='#fef0c7'
                              />
                              {/* <Chip
                                size='small'
                                label={`${lineRows.length} item${lineRows.length === 1 ? '' : 's'}`}
                                sx={{
                                  backgroundColor: '#fff',
                                  border: '1px solid #e4e7ec',
                                  fontWeight: 600,
                                }}
                              /> */}
                            </Box>
                            )}
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {isMobile ? (
                            lineRows.length === 0 ? (
                              <Typography
                                variant='body2'
                                sx={{ p: 2, color: 'text.secondary' }}
                              >
                                No items in this section.
                              </Typography>
                            ) : (
                              lineRows.map((row, index) => (
                                <ElementItemRow
                                  key={row.id}
                                  row={row}
                                  index={index}
                                  mobile
                                  saving={savingId === row.id}
                                  canEditReview
                                  onSave={saveRow}
                                  onSaveReview={saveReview}
                                  onDelete={(item) =>
                                    setConfirm({ type: 'delete', row: item })
                                  }
                                  highlighted={String(focusItemId) === String(row.id)}
                                />
                              ))
                            )
                          ) : (
                            <TableContainer
                              sx={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}
                            >
                              <Table
                                size='small'
                                sx={{
                                  width: '100%',
                                  tableLayout: 'fixed',
                                  borderCollapse: 'separate',
                                  '& input:focus': {
                                    outline: 'none',
                                    borderColor: '#be210b',
                                    boxShadow:
                                      '0 0 0 2px rgba(190, 33, 11, 0.12)',
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    {[
                                      ['Sr', '5%'],
                                      ['Particulars', structureVerifier ? '16%' : '14%'],
                                      ['Dimensions', '9%'],
                                      ['Size', '6%'],
                                      ['Unit', '10%'],
                                      ['Quantity', '6%'],
                                      ['Status', '12%'],
                                      ['Remarks', structureVerifier ? '32%' : '18%'],
                                      ...(structureVerifier ? [] : [['Status Updated By', '16%']]),
                                      ['', '4%'],
                                    ].map(([label, width]) => (
                                      <TableCell
                                        key={label}
                                        sx={{
                                          width,
                                          backgroundColor: '#2F3542',
                                          color: '#fff',
                                          fontWeight: 600,
                                          fontSize: '0.68rem',
                                          letterSpacing: '0.04em',
                                          textTransform: 'uppercase',
                                          p: 1.8,
                                          whiteSpace: 'normal',
                                          lineHeight: 1.2,
                                          borderBottom: 'none',
                                        }}
                                      >
                                        {label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {lineRows.map((row, index) => (
                                    <ElementItemRow
                                      key={row.id}
                                      row={row}
                                      index={index}
                                      saving={savingId === row.id}
                                      canEditReview
                                      showUpdatedBy={!structureVerifier}
                                      onSave={saveRow}
                                      onSaveReview={saveReview}
                                      onDelete={(item) =>
                                        setConfirm({ type: 'delete', row: item })
                                      }
                                      highlighted={String(focusItemId) === String(row.id)}
                                    />
                                  ))}
                                  {lineRows.length === 0 && (
                                    <TableRow>
                                      <TableCell colSpan={structureVerifier ? 9 : 10}>
                                        No items in this section.
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              px: 1.5,
                              py: 1,
                              borderTop: '1px solid #f3e8e6',
                              backgroundColor: '#fff',
                            }}
                          >
                            <Button
                              size='small'
                              startIcon={<AddIcon />}
                              onClick={() => openAddItem(section)}
                              sx={{
                                textTransform: 'none',
                                color: '#be210b',
                                fontWeight: 600,
                              }}
                            >
                              ADD ITEM
                            </Button>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                  {sections.length === 0 && (
                    <Typography
                      variant='body2'
                      sx={{ py: 2, color: 'text.secondary' }}
                    >
                      {structureVerifier && !search.trim()
                        ? 'No structures are assigned to this user.'
                        : 'No matching items.'}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        </Paper>
        <Dialog
          open={Boolean(confirm)}
          onClose={() => !clearing && setConfirm(null)}
          aria-labelledby='clear-file-title'
          PaperProps={{ sx: dialogPaperSx }}
        >
          {confirm?.type === 'delete' ? (
            <>
              <Box sx={dialogHeaderSx}>
                <Box sx={dangerIconSx}>
                  <DeleteOutlineIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                  <Typography id='clear-file-title' sx={dialogTitleSx}>
                    Delete item
                  </Typography>
                  <Typography sx={dialogHintSx}>
                    This item will be removed from the section. This cannot be undone.
                  </Typography>
                </Box>
                <IconButton
                  size='small'
                  aria-label='Close'
                  disabled={clearing}
                  onClick={() => setConfirm(null)}
                  sx={dialogCloseSx}
                >
                  <CloseIcon fontSize='small' />
                </IconButton>
              </Box>
              <DialogContent sx={{ px: 3, pt: 2, pb: 0.5 }}>
                <Box sx={itemNameCardSx}>
                  <Typography sx={itemNameLabelSx}>Item name</Typography>
                  <Typography sx={itemNameValueSx}>
                    {(confirm.row?.particulars || 'Untitled item').toUpperCase()}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={dialogActionsSx}>
                <Button
                  onClick={() => setConfirm(null)}
                  disabled={clearing}
                  sx={cancelButtonSx}
                >
                  CANCEL
                </Button>
                <Button
                  variant='contained'
                  onClick={handleConfirm}
                  disabled={clearing}
                  sx={dangerButtonSx}
                >
                  {clearing ? 'Deleting...' : 'DELETE'}
                </Button>
              </DialogActions>
            </>
          ) : (
            <>
              <DialogTitle id='clear-file-title'>
                {confirm?.type === 'replace' ? 'Replace file?' : 'Clear file?'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {confirm?.type === 'replace'
                    ? 'The current element sheet will be replaced by the new file. Existing items for this event will be overwritten.'
                    : 'This will remove the uploaded element items for this event. This cannot be undone.'}
                </DialogContentText>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setConfirm(null)}
                  disabled={clearing}
                  sx={{ textTransform: 'none' }}
                >
                  CANCEL
                </Button>
                <Button
                  variant='contained'
                  onClick={handleConfirm}
                  disabled={clearing}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#be210b',
                    '&:hover': { backgroundColor: '#a61c09' },
                  }}
                >
                  {clearing
                    ? 'Clearing...'
                    : confirm?.type === 'replace'
                      ? 'REPLACE'
                      : 'CLEAR'}
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
        <Dialog
          open={Boolean(addItem)}
          onClose={() => !adding && setAddItem(null)}
          fullWidth
          maxWidth='xs'
          PaperProps={{ sx: dialogPaperSx }}
        >
          <Box sx={dialogHeaderSx}>
            <Box sx={addIconSx}>
              <AddIcon />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={dialogTitleSx}>Add item</Typography>
              {/* <Typography sx={dialogHintSx}>
                Add a new item to this section.
              </Typography> */}
            </Box>
            <IconButton
              size='small'
              aria-label='Close'
              disabled={adding}
              onClick={() => setAddItem(null)}
              sx={dialogCloseSx}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
          <DialogContent sx={{ px: 3, pt: 2, pb: 0.5 }}>
            {(addItem?.code || addItem?.title) && (
              <Box sx={sectionChipSx}>
                {addItem?.code && (
                  <Box component='span' sx={sectionCodeSx}>
                    {addItem.code}
                  </Box>
                )}
                <Typography component='span' sx={sectionTitleSx}>
                  {addItem?.title}
                </Typography>
              </Box>
            )}
            <TextField
              autoFocus
              fullWidth
              // label='Item name'
              placeholder='Enter item name'
              value={newParticulars}
              onChange={(event) => setNewParticulars(event.target.value.toUpperCase())}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && newParticulars.trim()) handleAddItem();
              }}
              sx={addItemFieldSx}
            />
          </DialogContent>
          <DialogActions sx={dialogActionsSx}>
            <Button
              onClick={() => setAddItem(null)}
              disabled={adding}
              sx={cancelButtonSx}
            >
              CANCEL
            </Button>
            <Button
              variant='contained'
              onClick={handleAddItem}
              disabled={adding || !newParticulars.trim()}
              sx={primaryButtonSx}
            >
              {adding ? 'Adding...' : 'ADD ITEM'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );

  return (
    <LayoutContainer>
      <AdminHeader title='LOCATION & STRUCTURE' />
      {pageBody}
    </LayoutContainer>
  );
};

export default LocationStructure;
